import { useCallback, useEffect, useRef, useState } from 'react';
import { isCriticalAlert } from '../monitoringUtils';
import { fetchRecentAlerts } from './useVehicleAlerts';

export interface AlertSummary {
    total: number;
    critical: number;
}

const usePageAlerts = (assetIds: string[], enabled: boolean) => {
    const cache = useRef(new Map<string, AlertSummary>());
    const [version, setVersion] = useState(0);
    const idsKey = assetIds.join(',');

    useEffect(() => {
        if (!enabled) {
            if (cache.current.size) {
                cache.current.clear();
                setVersion(value => value + 1);
            }
            return;
        }
        const missing = assetIds.filter(id => !cache.current.has(id));
        if (!missing.length) {
            return;
        }

        let cancelled = false;
        fetchRecentAlerts(missing).then(
            result => {
                if (cancelled) {
                    return;
                }
                const summaries = new Map<string, AlertSummary>(missing.map(id => [id, { total: 0, critical: 0 }]));
                for (const alert of result) {
                    const summary = summaries.get(String(alert?.vehicleId ?? ''));
                    if (summary) {
                        summary.total += 1;
                        summary.critical += isCriticalAlert(alert) ? 1 : 0;
                    }
                }
                for (const [id, summary] of summaries) {
                    cache.current.set(id, summary);
                }
                setVersion(value => value + 1);
            },
            () => undefined
        );

        return () => {
            cancelled = true;
        };
    }, [idsKey, enabled]);

    const getAlertSummary = useCallback((assetId: string) => cache.current.get(assetId), [version]);

    return { getAlertSummary };
};

export default usePageAlerts;
